'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { useAppTargetId, useProjectStore } from '../../../store/projectStore';

export type BrowserExtensionInfo = {
  id: string;
  name: string;
  version: string;
  description?: string;
  enabled: boolean;
  source: 'folder' | 'crx' | 'webstore';
};

export type BrowserExtensionLoadingState =
  | 'idle'
  | 'folder'
  | 'crx'
  | 'webstore';

type InstallResult = {
  success: boolean;
  data?: { name: string };
  error?: string;
};

const CHROME_WEB_STORE_URL_PATTERN =
  /(?:chrome\.google\.com\/webstore|chromewebstore\.google\.com)\/detail\/[^/]+\/([a-z]{32})/i;
const CHROME_EXTENSION_ID_PATTERN = /^[a-z]{32}$/;

export const resolveChromeExtensionId = (input: string): string | null => {
  const trimmed = input.trim();
  const extensionId =
    trimmed.match(CHROME_WEB_STORE_URL_PATTERN)?.[1] ?? trimmed;

  return CHROME_EXTENSION_ID_PATTERN.test(extensionId) ? extensionId : null;
};

export function useBrowserExtensions() {
  const t = useTranslations('settings.proxyForm.browser.toasts');
  const { selectedCompany } = useProjectStore();
  const appTargetId = useAppTargetId();
  const tenantId = selectedCompany?.tenantId ?? '';
  const [extensions, setExtensions] = useState<BrowserExtensionInfo[]>([]);
  const [loadingState, setLoadingState] =
    useState<BrowserExtensionLoadingState>('idle');
  const hasApplication = Boolean(appTargetId && tenantId);

  const loadExtensions = useCallback(async () => {
    if (!tenantId || !appTargetId || !window.electronAPI?.extensions) return;

    try {
      const result = await window.electronAPI.extensions.getAll(
        tenantId,
        appTargetId,
      );

      if (result.success && result.data) {
        setExtensions(result.data);
      }
    } catch (error) {
      console.error('Failed to load extensions:', error);
    }
  }, [tenantId, appTargetId]);

  useEffect(() => {
    if (appTargetId && tenantId) {
      loadExtensions();
    }
  }, [appTargetId, tenantId, loadExtensions]);

  const runInstall = async ({
    source,
    install,
    ignoredError,
    onInstalled,
  }: {
    source: Exclude<BrowserExtensionLoadingState, 'idle'>;
    install: () => Promise<InstallResult>;
    ignoredError?: string;
    onInstalled?: () => void;
  }) => {
    setLoadingState(source);

    try {
      const result = await install();

      if (result.success && result.data) {
        toasterApi.success({
          title: t('installedTitle'),
          description: t('installedDescription', { name: result.data.name }),
        });
        onInstalled?.();
        await loadExtensions();
      } else if (result.error && result.error !== ignoredError) {
        toasterApi.error({
          title: t('installFailedTitle'),
          description: result.error,
        });
      }
    } catch (error) {
      toasterApi.error({
        title: t('installFailedTitle'),
        description:
          error instanceof Error
            ? error.message
            : t('installFailedDescription'),
      });
    } finally {
      setLoadingState('idle');
    }
  };

  const handleInstallFromFolder = async () => {
    const extensionsApi = window.electronAPI?.extensions;

    if (!extensionsApi || !appTargetId) return;
    await runInstall({
      source: 'folder',
      install: () => extensionsApi.installFromFolder(tenantId, appTargetId),
      ignoredError: 'No folder selected',
    });
  };

  const handleInstallFromCrx = async () => {
    const extensionsApi = window.electronAPI?.extensions;

    if (!extensionsApi || !appTargetId) return;
    await runInstall({
      source: 'crx',
      install: () => extensionsApi.installFromCrx(tenantId, appTargetId),
      ignoredError: 'No file selected',
    });
  };

  const handleInstallFromWebStore = async (webStoreUrl: string) => {
    const extensionsApi = window.electronAPI?.extensions;

    if (!extensionsApi || !appTargetId) return;
    if (!webStoreUrl.trim()) {
      toasterApi.error({
        title: t('invalidUrlTitle'),
        description: t('invalidUrlDescription'),
      });

      return;
    }

    const extensionId = resolveChromeExtensionId(webStoreUrl);

    if (!extensionId) {
      toasterApi.error({
        title: t('installFailedTitle'),
        description: t('invalidExtensionId'),
      });

      return;
    }

    await runInstall({
      source: 'webstore',
      install: () =>
        extensionsApi.installFromWebStore(tenantId, appTargetId, extensionId),
    });
  };

  const handleToggleExtension = async ({
    extensionId,
    enabled,
  }: {
    extensionId: string;
    enabled: boolean;
  }) => {
    if (!window.electronAPI?.extensions || !appTargetId) return;

    try {
      const result = await window.electronAPI.extensions.toggle(
        tenantId,
        appTargetId,
        extensionId,
        enabled,
      );

      if (result.success) {
        setExtensions((previous) =>
          previous.map((extension) =>
            extension.id === extensionId
              ? { ...extension, enabled }
              : extension,
          ),
        );
      } else if (result.error) {
        toasterApi.error({
          title: t('toggleFailedTitle'),
          description: result.error,
        });
      }
    } catch (error) {
      toasterApi.error({
        title: t('toggleFailedTitle'),
        description:
          error instanceof Error ? error.message : t('toggleFailedDescription'),
      });
    }
  };

  const handleUninstallExtension = async (extensionId: string) => {
    if (!window.electronAPI?.extensions || !appTargetId) return;

    try {
      const result = await window.electronAPI.extensions.uninstall(
        tenantId,
        appTargetId,
        extensionId,
      );

      if (result.success) {
        setExtensions((previous) =>
          previous.filter((extension) => extension.id !== extensionId),
        );
        toasterApi.success({
          title: t('uninstalledTitle'),
          description: t('uninstalledDescription'),
        });
      } else if (result.error) {
        toasterApi.error({
          title: t('uninstallFailedTitle'),
          description: result.error,
        });
      }
    } catch (error) {
      toasterApi.error({
        title: t('uninstallFailedTitle'),
        description:
          error instanceof Error
            ? error.message
            : t('uninstallFailedDescription'),
      });
    }
  };

  return {
    hasApplication,
    extensions,
    loadingState,
    handleInstallFromFolder,
    handleInstallFromCrx,
    handleInstallFromWebStore,
    handleToggleExtension,
    handleUninstallExtension,
  };
}
